import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { DEFAULT_DECIMAL_PLACES } from "../helpers"

export type GenericQuestion = {
  shouldSkip?: (questions: QuestionData[]) => boolean
  canBeSkipped?: boolean
}

export type NumberAnswerData = GenericQuestion & {
  type: "number"
  placeholder: string
  calculatePoints: (questions: QuestionData[]) => number
  value: number | null
  required: boolean
  validate: () => boolean
  validationError: string
  hint?: {
    text: string
  }
}

export type RadioAnswerData = GenericQuestion & {
  type: "radio"
  options: {
    text: string
    points: number
    selected: boolean
    hint?: {
      text: string
    }
  }[]
  required: boolean
}

export type SelectAnswerData = GenericQuestion & {
  type: "select"
  placeholder: string
  options: {
    text: string
    points: number
    selected: boolean
    hint?: {
      text: string
    }
  }[]
  required: boolean
}

export type Answer = NumberAnswerData | RadioAnswerData | SelectAnswerData

export type QuestionData = {
  analyticsStep: string
  number: string
  text: string
  answer: Answer
}

export const getScore = (answer: Answer, questions: QuestionData[]): number => {
  switch (answer.type) {
    case "number":
      return answer.calculatePoints(questions)
    case "radio":
    case "select":
      return (
        answer.options.find((o) => o.selected)?.points ??
        Math.min(...answer.options.map((o) => o.points))
      )
  }
}

export const getPrevQuestionIndex = (
  currentIndex: number,
  questions: QuestionData[],
): number | null => {
  const prevIndexIndex = currentIndex - 1
  for (let i = prevIndexIndex; i >= 0; i--) {
    const q = questions[i]
    if (q.answer.shouldSkip && q.answer.shouldSkip(questions)) continue
    return i
  }
  return null
}

export const getNextQuestionIndex = (
  currentIndex: number,
  questions: QuestionData[],
): number | null => {
  const nextIndex = currentIndex + 1
  for (let i = nextIndex; i < questions.length; i++) {
    const q = questions[i]
    if (q.answer.shouldSkip && q.answer.shouldSkip(questions)) continue
    return i
  }
  return null
}

export const getFirstIndexOfNonSkippableQuestion = (
  currentIndex: number,
  questions: QuestionData[],
) => {
  const nextIndex = currentIndex + 1
  for (let i = nextIndex; i < questions.length; i++) {
    const q = questions[i]

    if (!q.answer.canBeSkipped) {
      return i
    }
  }
  return null
}

export const getAnsweredQuestions = (questions: QuestionData[]) => {
  return questions
    .filter(
      ({ answer }) => !(answer.shouldSkip && answer.shouldSkip(questions)),
    )
    .filter(({ answer }) => isAnswered(answer, questions))
}

export const calculateScore = (questions: QuestionData[]): number => {
  let currentIndex: number | null = 0
  let score = 0

  do {
    const currentQuestion = questions[currentIndex]
    if (isAnswered(currentQuestion.answer, questions))
      score += getScore(currentQuestion.answer, questions)
    currentIndex = getNextQuestionIndex(currentIndex, questions)
  } while (currentIndex !== null)

  return score
}

export const summarizeQuestions = (questions: QuestionData[]) =>
  getAnsweredQuestions(questions)
    .map(({ number, text, answer }) => {
      const qLine = `${number}) ${text}`

      if (answer.type === "number") {
        return `${qLine} - ${(answer as NumberAnswerData).value}`
      } else if (answer.type === "radio") {
        return `${qLine} - ${
          (answer as RadioAnswerData).options.find((o) => o.selected)?.text
        }`
      } else if (answer.type === "select") {
        return `${qLine} - ${(answer as SelectAnswerData).options
          .filter((o) => o.selected)
          .map((o) => o.text)
          .join(", ")}`
      } else {
        return `${qLine} - not answered`
      }
    })
    .join("\n")

type Range = [number, number]
const mapToRange = (
  input: Range,
  output: Range,
  inputValue: number,
): number => {
  return (
    ((inputValue - input[0]) / (input[1] - input[0])) *
      (output[1] - output[0]) +
    output[0]
  )
}

const clamp = (min: number, max: number, value: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * Calculates relative score based on questionaire rules for score
 * @param questions
 * @returns relative score in range from 0 to 100 (inclusive)
 */
export const calculateRelativeScore = (questions: QuestionData[]): number => {
  const firstQuestion = questions[0]
  const isTechnician =
    firstQuestion.answer.type === "radio" &&
    firstQuestion.answer.options[0].selected

  const score = calculateScore(questions)

  const technicianScoreRange: Range = [20, 100]
  const nonTechnicianScoreRange: Range = [9, 47]
  const relativeScore = mapToRange(
    isTechnician ? technicianScoreRange : nonTechnicianScoreRange,
    [0, 100],
    score,
  ).toFixed(DEFAULT_DECIMAL_PLACES)

  return clamp(0, 100, Number(relativeScore))
}

export const isAnswered = (
  answer: Answer,
  questions: QuestionData[],
): boolean => {
  if (!answer.required && !answer.canBeSkipped) return true
  if (answer.shouldSkip && answer.shouldSkip(questions)) return true

  switch (answer.type) {
    case "number":
      return answer.validate()
    case "radio":
    case "select":
      return answer.options.find((o) => o.selected) !== undefined
  }
}

const skipWhenRadioAnswered = (
  questionIndex: number,
  answerIndex: number,
): ((questions: QuestionData[]) => boolean) => {
  return (questions) => {
    const question = questions[questionIndex]
    if (!question) return true
    if (question.answer.type !== "radio") return true

    return question.answer.options[answerIndex]?.selected ?? true
  }
}

export const useInitialQuestions: () => QuestionData[] = () => {
  const { t } = useTranslation()

  const initialQuestions = useMemo<QuestionData[]>(
    () => [
      {
        analyticsStep: "Q1",
        number: t("question1.nr"),
        text: t("question1.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question1.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question1.hint.text.answer.no"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q2",
        number: t("question2.nr"),
        text: t("question2.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.upTo5Years"),
              hint: {
                text: t("question2.hint.text.answer.upTo5Years"),
              },
            },
            {
              points: 3,
              selected: false,
              text: t("answer.from5To15years"),
              hint: {
                text: t("question2.hint.text.answer.from5To15years"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.moreThan15Years"),
              hint: {
                text: t("question2.hint.text.answer.moreThan15Years"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q3",
        number: t("question3.nr"),
        text: t("question3.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question3.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question3.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question3.hint.text.answer.idk"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q4",
        number: t("question4.nr"),
        text: t("question4.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 1,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question4.hint.text.answer.yes"),
              },
            },
            {
              points: 5,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question4.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question4.hint.text.answer.idk"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q5",
        number: t("question5.nr"),
        text: t("question5.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question5.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question5.hint.text.answer.no"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q5a",
        number: t("question5a.nr"),
        text: t("question5a.question"),
        answer: {
          type: "number",
          validate() {
            return this.value !== null && this.value > 0
          },
          validationError: t("answer.kWhInvalid"),
          calculatePoints: () => 0,
          placeholder: t("unit.kWh"),
          value: null,
          shouldSkip: skipWhenRadioAnswered(4, 1),
          required: true,
          canBeSkipped: true,
        },
      },
      {
        analyticsStep: "Q5b",
        number: t("question5b.nr"),
        text: t("question5b.question"),
        answer: {
          type: "number",
          validate() {
            return this.value !== null && this.value > 0
          },
          validationError: t("answer.m2Invalid"),
          calculatePoints(questions) {
            const kWhQuestion = questions[5]
            if (kWhQuestion.answer.type !== "number") return 0

            const kWh = kWhQuestion.answer.value
            const m2 = this.value
            if (m2 === null || kWh === null) return 0
            if (m2 === 0) return 0

            const kWhM2 = kWh / m2

            if (kWhM2 <= 50) return 5
            if (kWhM2 <= 90) return 4
            if (kWhM2 <= 150) return 3
            if (kWhM2 <= 230) return 2
            if (kWhM2 <= 450) return 1
            return 0
          },
          placeholder: t("unit.m2"),
          value: null,
          required: true,
          shouldSkip: skipWhenRadioAnswered(4, 1),
          canBeSkipped: true,
        },
      },
      {
        analyticsStep: "Q6",
        number: t("question6.nr"),
        text: t("question6.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.last1To5Years"),
              hint: {
                text: t("question6.hint.text.answer.last1To5Years"),
              },
            },
            {
              points: 3,
              selected: false,
              text: t("answer.last5To10Years"),
              hint: {
                text: t("question6.hint.text.answer.last5To10Years"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.never"),
              hint: {
                text: t("question6.hint.text.answer.never"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q6a",
        number: t("question6a.nr"),
        text: t("question6a.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question6a.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question6a.hint.text.answer.no"),
              },
            },
          ],
          shouldSkip: skipWhenRadioAnswered(7, 2),
          required: true,
        },
      },
      {
        analyticsStep: "Q7",
        number: t("question7.nr"),
        text: t("question7.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 1,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question7.hint.text.answer.yes"),
              },
            },
            {
              points: 5,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question7.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question7.hint.text.answer.idk"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q8",
        number: t("question8.nr"),
        text: t("question8.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question8.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question8.hint.text.answer.no"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q9",
        number: t("question9.nr"),
        text: t("question9.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.annually"),
              hint: {
                text: t("question9.hint.text.answer.annually"),
              },
            },
            {
              points: 3,
              selected: false,
              text: t("answer.every2To3Years"),
              hint: {
                text: t("question9.hint.text.answer.every2To3Years"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.never"),
              hint: {
                text: t("question9.hint.text.answer.never"),
              },
            },
          ],
          required: true,
        },
      },
      {
        analyticsStep: "Q10",
        number: t("question10.nr"),
        text: t("question10.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.ballValves"),
              hint: {
                text: t("question10.hint.text.answer.ballValves"),
              },
            },
            {
              points: 4,
              selected: false,
              text: t("answer.globeValves"),
              hint: {
                text: t("question10.hint.text.answer.globeValves"),
              },
            },
            {
              points: 3,
              selected: false,
              text: t("answer.butterflyValves"),
              hint: {
                text: t("question10.hint.text.answer.butterflyValves"),
              },
            },
            {
              points: 2,
              selected: false,
              text: t("answer.shortStrokeValves"),
              hint: {
                text: t("question10.hint.text.answer.shortStrokeValves"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.paddleValves"),
              hint: {
                text: t("question10.hint.text.answer.paddleValves"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q11",
        number: t("question11.nr"),
        text: t("question11.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.electric"),
              hint: {
                text: t("question11.hint.text.answer.electric"),
              },
            },
            {
              points: 3,
              selected: false,
              text: t("answer.thermic"),
              hint: {
                text: t("question11.hint.text.answer.thermic"),
              },
            },
            {
              points: 2,
              selected: false,
              text: t("answer.pneumatic"),
              hint: {
                text: t("question11.hint.text.answer.pneumatic"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.noActuator"),
              hint: {
                text: t("question11.hint.text.answer.noActuator"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q12",
        number: t("question12.nr"),
        text: t("question12.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question12.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question12.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question12.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q13",
        number: t("question13.nr"),
        text: t("question13.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question13.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question13.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question13.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q14",
        number: t("question14.nr"),
        text: t("question14.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question14.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question14.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question14.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q15",
        number: t("question15.nr"),
        text: t("question15.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question15.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question15.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question15.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q16",
        number: t("question16.nr"),
        text: t("question16.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question16.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question16.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question16.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip: skipWhenRadioAnswered(0, 1),
          required: true,
        },
      },
      {
        analyticsStep: "Q16a",
        number: t("question16a.nr"),
        text: t("question16a.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.yes"),
              hint: {
                text: t("question16a.hint.text.answer.yes"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.no"),
              hint: {
                text: t("question16a.hint.text.answer.no"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question16a.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip(questions) {
            return (
              skipWhenRadioAnswered(0, 1)(questions) ||
              skipWhenRadioAnswered(18, 1)(questions) ||
              skipWhenRadioAnswered(18, 2)(questions)
            )
          },
          required: true,
        },
      },
      {
        analyticsStep: "Q16b",
        number: t("question16b.nr"),
        text: t("question16b.question"),
        answer: {
          type: "radio",
          options: [
            {
              points: 5,
              selected: false,
              text: t("answer.regularly"),
              hint: {
                text: t("question16b.hint.text.answer.regularly"),
              },
            },
            {
              points: 2,
              selected: false,
              text: t("answer.seldom"),
              hint: {
                text: t("question16b.hint.text.answer.seldom"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.never"),
              hint: {
                text: t("question16b.hint.text.answer.never"),
              },
            },
            {
              points: 1,
              selected: false,
              text: t("answer.idk"),
              hint: {
                text: t("question16b.hint.text.answer.idk"),
              },
            },
          ],
          shouldSkip(questions) {
            return (
              skipWhenRadioAnswered(0, 1)(questions) ||
              skipWhenRadioAnswered(18, 1)(questions) ||
              skipWhenRadioAnswered(18, 2)(questions)
            )
          },
          required: true,
        },
      },
    ],
    [t],
  )

  return initialQuestions
}
